import { Autocomplete, Box, MenuItem, Theme } from '@mui/material'
import { styled } from '@mui/system'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateMooringCache } from 'src/api/moorings'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { invalidateStorageCache } from 'src/api/storage'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { AppTextField } from 'src/components/AppTextField'
import ToggleGroupButtons from 'src/components/common/ToggleGroupButtons'
import { CaptionedCell } from 'src/components/table/CaptionedCell'
import EquipmentSendStorage from 'src/containers/storage/EquipmentSendStorage'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import { getMainComponentName } from 'src/utils/function'
import { z } from 'zod'
import { invalidateCertificateCache } from '../../../../api/Certificates'
import { invalidateCustomer, invalidateCustomersServiceStationDetails } from '../../../../api/customer'
import { invalidateLocalityCache } from '../../../../api/localities'
import {
  invalidateMainComponentCache,
  useAssignMainComponentToServiceStation,
  useGetMainComponentById,
  useSendToStorage,
} from '../../../../api/main-component'
import { invalidatePositionCache } from '../../../../api/positions'
import {
  invalidateComponentGetByStatus,
  invalidateGetServiceStationById,
  invalidateServiceStationCache,
  useGetAllEligibleServiceStations,
} from '../../../../api/service-station'
import { CancelButton } from '../../../../components/CancelButton'
import { AppForm, AppFormProps, FormSubmitButton } from '../../../../components/FormCore'
import { FlexSpinner } from '../../../../components/Spinner'
import { FacilityTypes, MainComponentStatus, MovementType, ServiceStationType } from '../../../../enums'
import { IMainComponent } from '../../../../generated-types/main-component'
import { IServiceStation } from '../../../../generated-types/service-station'
import { IFormSchemaReport } from '../../../../generated-types/form-schema'
import { useZodForm } from '../../../../hooks/zod-form'
import { ShowDialogProps } from '../../../../show-dialog'
import { invalidateScheduledTaskCache } from 'src/api/scheduledTask'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'

export const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primaryMain[theme.palette.mode],
  backgroundColor: getThemeColor(theme as Theme, MUIThemeColors.white),
  fontWeight: 700,
  zIndex: 1000,
}))

export const GroupItems = styled('ul')({
  padding: 0,
  zIndex: 100,
})

const MainComponentSchema = z.object({
  serviceStationId: z.number(),
  serviceStationType: z.number().optional(),
  formSchemaReports: z.array(z.any()).optional(),
})

export interface MainComponentSendFormProps extends AppFormProps {
  customerId: EntityId
  localityId: EntityId
  mooringId: EntityId
  positionId: EntityId
  id?: EntityId
  renderMainComponentSelectComponent?: (form: any) => JSX.Element
  storageId?: EntityId
}

const MainComponentSendForm: React.FC<MainComponentSendFormProps> = ({
  onSubmit,
  onCancel,
  customerId,
  initialValues,
  renderMainComponentSelectComponent,
}) => {
  const { t } = useTranslation(['position', 'common'])

  const form = useZodForm(MainComponentSchema, {
    defaultValues: { ...initialValues },
  })

  const { data, isLoading } = useGetAllEligibleServiceStations(customerId)

  if (isLoading) return <FlexSpinner />

  return (
    <AppForm form={form} onSubmit={onSubmit} hasInitialValues={initialValues}>
      {renderMainComponentSelectComponent && renderMainComponentSelectComponent(form)}
      <Autocomplete
        onChange={(_, value) => {
          form.setValue('serviceStationId', (value as IServiceStation).id)
          form.setValue('serviceStationType', (value as IServiceStation).type)
        }}
        options={(data ?? []).sort((a, b) => b.type.toString().localeCompare(a.type.toString()))}
        groupBy={(option: any) =>
          `${option.customer?.name} ${option.customer?.organizationNr ? `(${option?.customer?.organizationNr})` : ''}`
        }
        renderGroup={params => (
          <li>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        autoHighlight
        getOptionLabel={o => o.name}
        renderOption={(props, o) => (
          <MenuItem {...props} key={o.id} value={o.id}>
            {o.name}
          </MenuItem>
        )}
        renderInput={params => (
          <AppTextField
            {...params}
            label={t('select-service-station', { ns: 'main-component' })}
            inputProps={{
              ...params.inputProps,
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      <Box className="flex justify-end gap-4 pt-4">
        <FormSubmitButton createText={t('Send', { ns: 'common' })} />
        <CancelButton onClick={onCancel}>{t('cancel', { ns: 'common' })}</CancelButton>
      </Box>
    </AppForm>
  )
}

interface MainComponentCreateProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  mooringId: EntityId
  positionId: EntityId
  onlyServiceStation?: boolean
  storageId?: EntityId
  searchText?: string
  onSuccess?: () => void
}

interface MainComponentUpdateProps extends MainComponentCreateProps {
  id?: EntityId
  mainComponents?: IMainComponent[]
  localityIdFromURL?: EntityId
  onCloseMainDialog?: () => void
}

export const MainComponentSendModalComponent: React.FC<MainComponentUpdateProps> = ({ id, ...props }) => {
  if (id) {
    return <MainComponentSendModalComponentWithSingleComponent id={id} {...props} />
  }
  return <MainComponentSendModalGenericComponent {...props} />
}

interface MainComponentSendModalComponentWithSingleComponentProps extends MainComponentCreateProps {
  id: EntityId
}

export const MainComponentSendModalComponentWithSingleComponent: React.FC<
  MainComponentSendModalComponentWithSingleComponentProps
> = ({ customerId, id, ...rest }) => {
  const { data: mainComponent } = useGetMainComponentById(customerId, id)
  return (
    <MainComponentSendModalGenericComponent
      mainComponent={mainComponent}
      customerId={customerId}
      {...rest}
      isMainComponentAvailable={true}
    />
  )
}

interface MainComponentSendModalGenericComponentProps extends MainComponentCreateProps {
  mainComponent?: IMainComponent
  mainComponents?: IMainComponent[]
  isMainComponentAvailable?: boolean
  localityIdFromURL?: EntityId
  onCloseMainDialog?: () => void
}

export const MainComponentSendModalGenericComponent: React.FC<MainComponentSendModalGenericComponentProps> = ({
  pop,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponent,
  mainComponents,
  isMainComponentAvailable = false,
  onlyServiceStation = true,
  storageId,
  searchText,
  onSuccess,
  onCloseMainDialog,
}) => {
  const { t } = useTranslation(['position', 'common', 'main-component'])
  const showChecklistsDialog = useCompleteChecklistsDialog(customerId)
  const [facilityType, setFacilityType] = useState(FacilityTypes.serviceStation)
  const [selectedMainComponent, setSelectedMainComponent] = useState(mainComponent)
  const { mutateAsync } = useAssignMainComponentToServiceStation(customerId, selectedMainComponent?.id as EntityId)
  const { mutateAsync: sendToStorage } = useSendToStorage(customerId, selectedMainComponent?.id as EntityId)
  const { data: sendToServiceStationSchemas, isLoading: isLoadingSendToServiceStationSchemas } =
    useGetFormSchemasForMovement(customerId, {
      equipmentTypeIds: [selectedMainComponent?.type.id as number],
      movementTypes: [MovementType.SendToServiceStation],
    })
  const { data: sendToStorageSchemas, isLoading: isLoadingSendToStorageSchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds: [selectedMainComponent?.type.id as number], movementTypes: [MovementType.SendToStorage] },
  )
  useEffect(() => {
    if (mainComponent) {
      setSelectedMainComponent(mainComponent)
    }
  }, [mainComponent])

  useEffect(() => {
    if (mainComponents) {
      setSelectedMainComponent(mainComponents[0])
    }
  }, [mainComponents])

  const updateServiceStationComponents = useCallback(
    (serviceStationId, searchText) => {
      Object.keys(MainComponentStatus).filter(key =>
        invalidateComponentGetByStatus(customerId, serviceStationId, MainComponentStatus[key]),
      )
      invalidateGetServiceStationById(customerId, serviceStationId)
      invalidateMainComponentCache.getMainComponentsByComponentType(
        customerId,
        selectedMainComponent?.typeId as EntityId,
        searchText as string,
      )
      invalidateLocalityCache.getCageStatus(customerId, localityId)
      invalidateMooringCache.getGetMoorings(customerId as EntityId, localityId as EntityId, true, 0, 100000)
    },
    [customerId, selectedMainComponent],
  )

  const invalidations = useCallback(
    (form, serviceStationStatus) => {
      invalidateLocalityCache.getLocalityWithActivityCounts(customerId, localityId)
      invalidateLocalityCache.getLocality(customerId, localityId)
      invalidateMainComponentCache.getMainComponents(customerId, localityId, mooringId, positionId)
      invalidateCertificateCache.getCertificatesByPosition(customerId, positionId)
      invalidateCustomersServiceStationDetails()
      invalidateServiceStationCache.getServiceStations(customerId)
      invalidationActivityLogCache.getCageActivityLogs()
      updateServiceStationComponents(form['serviceStationId'], searchText)
      invalidateStorageCache.getEquipmentInStorage(customerId as EntityId, form.storageId as EntityId)
      invalidateStorageCache.getEquipmentInStorage(customerId, storageId as EntityId)
      invalidateComponentGetByStatus(customerId, form.serviceStationId, serviceStationStatus)
      invalidatePositionCache.getCagePositionDeviations(customerId, localityId, positionId)
      invalidateMainComponentCache.getMainComponentById(
        customerId as EntityId,
        (selectedMainComponent?.id as EntityId) ?? 0,
      )
      invalidateMainComponentCache.getEquipment(customerId)
      invalidateMooringCache.getMooringsForMap(customerId, localityId)
      invalidateMooringCache.getGetMooring(customerId, localityId, mooringId)
      invalidateMainComponentCache.invalidateEquipment(customerId, { localityId })
      invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId)
      invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId)
      invalidateMainComponentCache.getMaincomponentsBySearch(customerId, searchText as string)
      invalidateCertificateCache.getCertificates((selectedMainComponent?.id as EntityId) ?? 0)
      invalidateDeviation.getDeviations(customerId as EntityId)
      invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
      invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
      invalidateCustomer.getCustomersActivityCounts(customerId)
      invalidateLocalityCache.getMaintenances(customerId, localityId)
      invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
      invalidateDocument.getDocuments(customerId)
      invalidateDocument.getDocumentsForApproval(customerId, localityId)
      invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
      invalidateLocalityCache.getCageStatus(customerId, localityId)
      invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
      invalidateScheduledTaskCache.useGetEquipmentForWorkOrder(undefined, localityId as EntityId)
      invalidateScheduledTaskCache.useGetEquipmentOfWorkOrder(localityId as EntityId)
      onSuccess?.()
    },
    [customerId, localityId, mooringId, positionId, selectedMainComponent],
  )

  const performSendToServiceStation = async (form, reports: IFormSchemaReport[]) => {
    const serviceStationStatus =
      form.serviceStationType == ServiceStationType.Storage ? MainComponentStatus.InStock : MainComponentStatus.Incoming
    await mutateAsync(
      { ...form, formSchemaReports: reports },
      {
        onSuccess: () => {
          invalidations(form, serviceStationStatus)
          pop()
          toast(t('sendToServiceStationSuccess', { ns: 'main-component' }), { type: 'success' })
          onCloseMainDialog?.()
        },
      },
    )
  }

  const performSendToStorage = async (form, reports: IFormSchemaReport[]) => {
    await sendToStorage(
      { ...form, formSchemaReports: reports },
      {
        onSuccess: () => {
          invalidations(form, MainComponentStatus.InStock)
          pop()
          toast(t('sendToStorageSuccess', { ns: 'main-component' }), { type: 'success' })
          onCloseMainDialog?.()
        },
      },
    )
  }

  const onSubmit = async form => {
    await showChecklistsDialog({
      schemas: sendToServiceStationSchemas,
      isLoadingSchemas: isLoadingSendToServiceStationSchemas,
      onConfirm: reports => performSendToServiceStation(form, reports),
    })
  }

  const onSubmitSendToStorage = async form => {
    await showChecklistsDialog({
      schemas: sendToStorageSchemas,
      isLoadingSchemas: isLoadingSendToStorageSchemas,
      onConfirm: reports => performSendToStorage(form, reports),
    })
  }

  const renderMainComponentSelectComponent = useCallback(form => {
    return (
      <Autocomplete
        onChange={(_, value) => {
          form.setValue('mainComponentId', (value as IMainComponent).id)
          setSelectedMainComponent(value as IMainComponent)
        }}
        options={mainComponents ?? []}
        autoHighlight
        getOptionLabel={o =>
          [o.description, getMainComponentName(o.type.name), o.serialNumber].filter(Boolean).join(' ')
        }
        renderOption={(props, o) => {
          const typeName = getMainComponentName(o.type.name)

          return (
            <MenuItem {...props} key={o.id} value={o.id}>
              <CaptionedCell
                primary={o.description || typeName}
                caption={`${typeName} | ${o.serialNumber}`}
                sx={{ py: 0.5 }}
              />
            </MenuItem>
          )
        }}
        renderInput={params => (
          <AppTextField
            label={t('mainComponent', { ns: 'main-component' })}
            {...params}
            inputProps={{
              ...params.inputProps,
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        defaultValue={(mainComponents ?? [])[0] ?? undefined}
      />
    )
  }, [])

  return (
    <>
      <Box width={'370px'} paddingLeft={'10px'}>
        {!onlyServiceStation && (
          <Padding.pb8>
            <ToggleGroupButtons
              actions={[
                { title: t('service-station', { ns: 'page-container' }), value: FacilityTypes.serviceStation },
                { title: t('storage', { ns: 'page-container' }), value: FacilityTypes.storage },
              ]}
              defaultValue={FacilityTypes.serviceStation}
              value={facilityType}
              setViewMode={value => setFacilityType(value)}
              buttonWidth="180px"
            />
          </Padding.pb8>
        )}
      </Box>
      {facilityType == FacilityTypes.storage ? (
        <EquipmentSendStorage
          onSubmit={onSubmitSendToStorage}
          onCancel={() => pop()}
          customerId={customerId}
          localityId={localityId}
          mooringId={mooringId}
          positionId={positionId}
          renderMainComponentSelectComponent={
            !isMainComponentAvailable ? renderMainComponentSelectComponent : undefined
          }
          storageId={storageId}
        />
      ) : (
        <MainComponentSendForm
          onSubmit={onSubmit}
          onCancel={() => pop()}
          customerId={customerId}
          localityId={localityId}
          mooringId={mooringId}
          positionId={positionId}
          renderMainComponentSelectComponent={
            !isMainComponentAvailable ? renderMainComponentSelectComponent : undefined
          }
        />
      )}
    </>
  )
}
